import { Button } from '@/components/ui/button';
import {
  AlignStartHorizontal,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/features/i18n/locales';

export type AlignmentType =
  | 'left'
  | 'center-h'
  | 'right'
  | 'top'
  | 'center-v'
  | 'bottom';

interface AlignmentButtonsProps {
  onAlign: (alignment: AlignmentType) => void;
  disabled?: boolean;
  className?: string;
}

const getHorizontalAlignments = (t: any): Array<{
  type: AlignmentType;
  icon: typeof AlignStartVertical;
  label: string;
}> => [
    { type: 'left', icon: AlignStartVertical, label: t.properties.alignLeft },
    { type: 'center-h', icon: AlignCenterVertical, label: t.properties.alignCenterH },
    { type: 'right', icon: AlignEndVertical, label: t.properties.alignRight },
  ];

const getVerticalAlignments = (t: any): Array<{
  type: AlignmentType;
  icon: typeof AlignStartHorizontal;
  label: string;
}> => [
    { type: 'top', icon: AlignStartHorizontal, label: t.properties.alignTop },
    { type: 'center-v', icon: AlignCenterHorizontal, label: t.properties.alignCenterV },
    { type: 'bottom', icon: AlignEndHorizontal, label: t.properties.alignBottom },
  ];

/**
 * Horizontal and vertical alignment button groups.
 * Used to align selected clips to canvas edges or center.
 */
export function AlignmentButtons({
  onAlign,
  disabled = false,
  className,
}: AlignmentButtonsProps) {
  const t = useTranslation();

  return (
    <div className={cn('flex items-center gap-3', className)}>
      {/* Horizontal alignment */}
      <div className="flex items-center gap-0.5 p-0.5 bg-secondary rounded-md">
        {getHorizontalAlignments(t).map(({ type, icon: Icon, label }) => (
          <Button
            key={type}
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onAlign(type)}
            disabled={disabled}
            data-tooltip={label}
            data-tooltip-side="bottom"
          >
            <Icon className="w-3.5 h-3.5" />
          </Button>
        ))}
      </div>

      {/* Vertical alignment */}
      <div className="flex items-center gap-0.5 p-0.5 bg-secondary rounded-md">
        {getVerticalAlignments(t).map(({ type, icon: Icon, label }) => (
          <Button
            key={type}
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onAlign(type)}
            disabled={disabled}
            data-tooltip={label}
            data-tooltip-side="bottom"
          >
            <Icon className="w-3.5 h-3.5" />
          </Button>
        ))}
      </div>
    </div>
  );
}
